/**
 * apps/web/src/App.tsx
 *
 * Route table for the glarno product UI ("Oggi" Mobile Action Inbox).
 *
 * Structure:
 *   /login              public (no auth, no shell)
 *   everything else     <PrivateRoute> → <Layout> → lazy page
 *
 * Every section is a React.lazy() page so each becomes its own JS chunk. The
 * navigation sections mirror docs/07-ux/navigation-and-screens.md.
 */

import React, { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, type Location } from 'react-router-dom';
import { Layout } from './components/Layout';
import { RouteDrawer } from './components/RouteDrawer';
import { PrivateRoute } from './components/PrivateRoute';
import { Spinner } from './components/ui/Spinner';
import { useStrings } from './i18n';

// Public
const Login = React.lazy(() => import('./pages/Login'));
const Start = React.lazy(() => import('./pages/Start'));
const MagicLink = React.lazy(() => import('./pages/MagicLink'));

// Authenticated, full-screen onboarding wizard (no app shell).
const Onboarding = React.lazy(() => import('./pages/Onboarding'));

// Authenticated sections
const Oggi = React.lazy(() => import('./pages/Oggi'));
const Clienti = React.lazy(() => import('./pages/Clienti'));
const ClienteDetail = React.lazy(() => import('./pages/ClienteDetail'));
const Progetti = React.lazy(() => import('./pages/Progetti'));
const ProgettoDetail = React.lazy(() => import('./pages/ProgettoDetail'));
const Soldi = React.lazy(() => import('./pages/Soldi'));
const Documenti = React.lazy(() => import('./pages/Documenti'));
const Messaggi = React.lazy(() => import('./pages/Messaggi'));
const Team = React.lazy(() => import('./pages/Team'));
const Report = React.lazy(() => import('./pages/Report'));
const Libreria = React.lazy(() => import('./pages/Libreria'));
const Tabella = React.lazy(() => import('./pages/Tabella'));
const Governance = React.lazy(() => import('./pages/Governance'));
const Automazioni = React.lazy(() => import('./pages/Automazioni'));
const Impostazioni = React.lazy(() => import('./pages/Impostazioni'));
const Abbonamento = React.lazy(() => import('./pages/Abbonamento'));
const Integrazioni = React.lazy(() => import('./pages/Integrazioni'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

function PageLoader(): React.ReactElement {
  const strings = useStrings();
  return (
    <div className="flex h-[60vh] items-center justify-center gap-3 text-sm text-muted">
      <Spinner /> {strings.common.loading}
    </div>
  );
}

export default function App(): React.ReactElement {
  // Background-location pattern: when a detail route is opened from within the app
  // (a row click sets location.state.background), render the list underneath and
  // the detail in a RouteDrawer on top. A deep-link / hard-refresh has no
  // background, so it falls through to the full-page detail route below.
  const location = useLocation();
  const state = location.state as { background?: Location } | null;
  const background = state?.background;
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes location={background ?? location}>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/start" element={<Start />} />
        <Route path="/auth/magic-link" element={<MagicLink />} />

        {/* Authenticated, full-screen (no app shell): the onboarding wizard */}
        <Route element={<PrivateRoute />}>
          <Route path="/onboarding" element={<Onboarding />} />
        </Route>

        {/* Authenticated app shell */}
        <Route element={<PrivateRoute />}>
          <Route element={<Layout />}>
            <Route index element={<Navigate to="/oggi" replace />} />
            <Route path="/oggi" element={<Oggi />} />

            <Route path="/clienti" element={<Clienti />} />
            <Route path="/clienti/:id" element={<ClienteDetail />} />

            <Route path="/progetti" element={<Progetti />} />
            <Route path="/progetti/:id" element={<ProgettoDetail />} />

            <Route path="/soldi" element={<Soldi />} />
            <Route path="/documenti" element={<Documenti />} />
            <Route path="/messaggi" element={<Messaggi />} />
            <Route path="/team" element={<Team />} />
            <Route path="/report" element={<Report />} />
            <Route path="/libreria" element={<Libreria />} />
            <Route path="/tabella" element={<Tabella />} />
            <Route path="/governance" element={<Governance />} />
            <Route path="/automazioni" element={<Automazioni />} />

            <Route path="/impostazioni" element={<Impostazioni />} />
            <Route path="/impostazioni/abbonamento" element={<Abbonamento />} />
            {/* Canonical path is the OAuth callback's default return target;
                /impostazioni/integrazioni is the localized alias. */}
            <Route path="/settings/integrations" element={<Integrazioni />} />
            <Route path="/impostazioni/integrazioni" element={<Integrazioni />} />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>

      {/* Detail-as-drawer overlay: only when navigated from within the app. */}
      {background ? (
        <Routes>
          <Route
            path="/clienti/:id"
            element={
              <RouteDrawer>
                <ClienteDetail inDrawer />
              </RouteDrawer>
            }
          />
        </Routes>
      ) : null}
    </Suspense>
  );
}
